import { useState, useMemo, useEffect } from "react";
import React from "react";
import { CryptoData } from "api/minmax/useFetchMinMax";
import { QuotesData } from "api/prices/useFetchPrices";
import { SecurityCode } from "api/securities";
import { round } from "utils/number";
import { getDecimalPlaces } from "views/trading/utils";
import { useModifiedTranslation } from "./useModifiedTranslation";
import {
  validateAmountTrade,
  validateUnitsTrade,
  isLimitOrderOnly,
} from "../components/TradingModals/BuySellModalContent/utils";

const DEFAULT_ALLOWED_SLIPPAGE_PERCENTAGE = 0.01;
const ADVANCED_ALLOWED_SLIPPAGE_PERCENTAGE = 0.003;

const roundDown = (value: number, digits: number) => {
  const factor = Math.pow(10, digits);
  return Math.floor(value * factor) / factor;
};

interface UseTradingFormProps {
  availableCash: number;
  availableHoldingAmount: number;
  securityName: string;
  securityCode: string;
  minMaxData: CryptoData;
  quotesData: QuotesData;
  initialOperation?: "buy" | "sell";
  defaultToMarket?: boolean;
  useAdvancedSlippage?: boolean;
}

export type OrderType = "Market" | "Limit" | "StopLimit";

export const useTradingForm = ({
  availableCash,
  availableHoldingAmount,
  securityName,
  securityCode,
  minMaxData,
  quotesData,
  initialOperation = "buy",
  defaultToMarket = false,
  useAdvancedSlippage = false,
}: UseTradingFormProps) => {
  const isLimitOnly = isLimitOrderOnly(securityCode as SecurityCode);
  const [actionType, setActionType] = useState<"buy" | "sell">(
    initialOperation
  );

  const { t } = useModifiedTranslation();
  const [amount, setAmount] = useState("");
  const [limitOrderPrice, setLimitOrderPrice] = useState("");
  const [triggerPrice, setTriggerPrice] = useState("");
  const [inputError, setInputError] = useState("");
  const [tradeType, setTradeType] = useState<"units" | "amount">("units");
  const [orderType, setOrderType] = useState<OrderType>(
    isLimitOnly ? "Limit" : defaultToMarket ? "Market" : "Limit"
  );

  const initialSlippagePercentage = useAdvancedSlippage
    ? ADVANCED_ALLOWED_SLIPPAGE_PERCENTAGE
    : DEFAULT_ALLOWED_SLIPPAGE_PERCENTAGE;
  const initialSlippageInput = (initialSlippagePercentage * 100).toFixed(2);

  const [allowedSlippagePercentage, setAllowedSlippagePercentage] =
    useState<number>(initialSlippagePercentage);
  const [allowedSlippageInput, setAllowedSlippageInput] =
    useState<string>(initialSlippageInput);
  const [endTime, setEndTime] = useState<string>("");

  const handleOrderTypeChange = (newType: OrderType) => {
    resetForm();

    if (newType === "Limit" || newType === "StopLimit") {
      setTradeType("units");
    }

    setOrderType(newType);
  };

  const unitPrice = Number(
    (actionType === "buy"
      ? quotesData[securityName]?.buy
      : quotesData[securityName]?.sell) || 0
  );

  const unitPriceToUse = useMemo(() => {
    if (orderType === "Limit") {
      return Number(limitOrderPrice);
    }
    return unitPrice;
  }, [orderType, limitOrderPrice, unitPrice]);

  // When buying, add slippage, when selling, subtract slippage
  const unitPriceWithSlippage = useMemo(() => {
    const priceWithSlippage =
      unitPrice *
      (1 + allowedSlippagePercentage * (actionType === "buy" ? 1 : -1));

    // For amount-based trades (EUR), always use 2 decimals
    // For unit-based trades, use the decimal places from minMaxData
    const decimalPlaces =
      tradeType === "amount"
        ? 2
        : getDecimalPlaces(minMaxData[securityName]?.minPriceIncrement);

    return priceWithSlippage.toFixed(decimalPlaces);
  }, [
    unitPrice,
    allowedSlippagePercentage,
    actionType,
    tradeType,
    minMaxData,
    securityName,
  ]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.currentTarget.value.replace(/,/g, ".");
    const regex = /^(0(\.\d*)?|[1-9]\d*(\.\d*)?)$/;
    if (regex.test(inputValue) || inputValue === "") {
      setAmount(inputValue);
    }

    handleValidateAmount(inputValue);
  };

  const handleLimitOrderAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.currentTarget.value.replace(/,/g, ".");
    const regex = /^(0(\.\d*)?|[1-9]\d*(\.\d*)?)$/;
    if (regex.test(inputValue) || inputValue === "") {
      setAmount(inputValue);
    }

    handleValidateAmountWithoutBalance(inputValue);
  };

  const handleShortcutClick = (percentage: number) => {
    if (actionType === "sell") {
      const newAmount = availableHoldingAmount * (percentage / 100);
      const roundFn = percentage === 100 ? roundDown : round;

      setAmount(roundFn(newAmount, 2).toString());
      return;
    }

    if (tradeType === "units") {
      if (!unitPrice) {
        return;
      }

      const cashToSpend = availableCash * (percentage / 100);
      const maxUnits = cashToSpend / unitPrice;
      const decimalPlaces = getDecimalPlaces(
        minMaxData[securityName]?.minPriceIncrement
      );

      const roundedUnits =
        Math.floor(maxUnits * Math.pow(10, decimalPlaces)) /
        Math.pow(10, decimalPlaces);

      setAmount(roundedUnits.toString());
      handleValidateAmount(roundedUnits.toString());
    } else if (tradeType === "amount") {
      // Round to the nearest integer since validation function errors with decimals
      const value = Math.floor(availableCash * (percentage / 100));

      setAmount(value.toString());
      handleValidateAmount(value.toString());
    } else {
      console.warn("Invalid trade type");
    }
  };

  const handleValidateAmount = React.useCallback(
    (inputValue: string) => {
      // Clear error if input is empty
      if (inputValue === "") {
        setInputError("");
        return;
      }

      const value = parseFloat(inputValue);

      if (actionType === "buy") {
        const cashNeeded =
          tradeType === "units"
            ? Number(unitPriceToUse) * Number(value)
            : Number(value);

        const enoughCash = !isNaN(availableCash) && availableCash >= cashNeeded;

        if (!enoughCash) {
          setInputError(t("tradingModal.insufficientFunds"));
          return;
        }
      } else if (actionType === "sell") {
        const holdingsNeeded =
          tradeType === "units"
            ? Number(value)
            : Number(value) / Number(unitPriceToUse);

        const enoughHoldings =
          !isNaN(availableHoldingAmount) &&
          availableHoldingAmount >= holdingsNeeded;

        if (!enoughHoldings) {
          setInputError(t("tradingModal.insufficientFunds"));
          return;
        }
      }

      if (tradeType === "units") {
        validateUnitsTrade(
          inputValue,
          setInputError,
          t,
          minMaxData[securityName]
        );
      }
      if (tradeType === "amount") {
        validateAmountTrade(
          inputValue,
          setInputError,
          t,
          Number(unitPriceWithSlippage),
          minMaxData[securityName]
        );
      }
    },
    [
      actionType,
      tradeType,
      unitPriceToUse,
      availableCash,
      availableHoldingAmount,
      securityName,
      unitPriceWithSlippage,
      minMaxData,
      t,
    ]
  );

  const handleValidateAmountWithoutBalance = React.useCallback(
    (inputValue: string) => {
      // Clear error if input is empty
      if (inputValue === "") {
        setInputError("");
        return;
      }

      // Skip balance validation - only validate against min/max trading limits
      if (tradeType === "units") {
        validateUnitsTrade(
          inputValue,
          setInputError,
          t,
          minMaxData[securityName]
        );
      }
      if (tradeType === "amount") {
        validateAmountTrade(
          inputValue,
          setInputError,
          t,
          Number(unitPriceWithSlippage),
          minMaxData[securityName]
        );
      }
    },
    [tradeType, securityName, unitPriceWithSlippage, minMaxData, t]
  );

  const prevSecurityCodeRef = React.useRef(securityCode);

  // Re-validate amount when securityCode changes
  React.useLayoutEffect(() => {
    if (
      securityCode &&
      prevSecurityCodeRef.current !== securityCode &&
      amount
    ) {
      prevSecurityCodeRef.current = securityCode;
      handleValidateAmount(amount);
    }
  }, [handleValidateAmount, amount, securityCode]);

  // Re-validate amount when limit order price changes for limit orders
  React.useEffect(() => {
    if (orderType === "Limit" && amount && limitOrderPrice) {
      handleValidateAmount(amount);
    }
  }, [orderType, limitOrderPrice, amount, handleValidateAmount]);

  // Reset orderType to Limit if security only allows limit orders
  useEffect(() => {
    if (isLimitOnly && orderType === "Market") {
      setOrderType("Limit");
      // Optionally reset other form parts if needed, like tradeType if "amount" is only for market buys
      // resetForm(); // Might be too aggressive, depends on requirements
    }
  }, [isLimitOnly, orderType]);

  const handleLimitOrderPriceChange = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    const inputValue = event.currentTarget.value.replace(/,/g, ".");
    const regex = /^(0(\.\d*)?|[1-9]\d*(\.\d*)?)$/;

    if (regex.test(inputValue) || inputValue === "") {
      setLimitOrderPrice(inputValue);

      handleValidateLimitOrderPrice(inputValue);
    }
  };

  const handleValidateLimitOrderPrice = (inputValue: string) => {
    // Clear error if input is empty
    if (inputValue === "" || amount === "") {
      setInputError("");
      return;
    }

    const limitPrice = parseFloat(inputValue);
    const amountValue = parseFloat(amount);

    if (isNaN(limitPrice) || isNaN(amountValue)) {
      return;
    }

    // For both buy and sell, validate based on the total cost/proceeds
    if (actionType === "buy") {
      const cashNeeded =
        tradeType === "units" ? limitPrice * amountValue : amountValue;

      const enoughCash = !isNaN(availableCash) && availableCash >= cashNeeded;

      if (!enoughCash) {
        setInputError(t("tradingModal.insufficientFunds"));
      } else {
        // Re-validate the amount with the new limit price
        handleValidateAmount(amount);
      }
    } else if (actionType === "sell") {
      // For sell orders, we still need to validate that we have enough holdings
      const holdingsNeeded =
        tradeType === "units" ? amountValue : amountValue / limitPrice;

      const enoughHoldings =
        !isNaN(availableHoldingAmount) &&
        availableHoldingAmount >= holdingsNeeded;

      if (!enoughHoldings) {
        setInputError(t("tradingModal.insufficientFunds"));
      } else {
        // Re-validate the amount with the new limit price
        handleValidateAmount(amount);
      }
    }
  };

  const handleTriggerPriceChange = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    const inputValue = event.currentTarget.value.replace(/,/g, ".");
    const regex = /^(0(\.\d*)?|[1-9]\d*(\.\d*)?)$/;

    if (regex.test(inputValue) || inputValue === "") {
      setTriggerPrice(inputValue);
    }
  };

  const resetForm = () => {
    setAmount("");
    setLimitOrderPrice("");
    setTriggerPrice("");
    setInputError("");
    setAllowedSlippageInput(initialSlippageInput);
    setAllowedSlippagePercentage(initialSlippagePercentage);
    setEndTime("");
  };

  const handleLimitShortcutClick = (option: string) => {
    const quoteData = quotesData[securityName];

    if (!quoteData) {
      console.warn("No quote data found");
      return;
    }

    const bid = Number(quoteData.sell);
    const offer = Number(quoteData.buy);
    const priceDecimalPlaces = getDecimalPlaces(
      minMaxData[securityName]?.minPriceIncrement
    );

    let newLimitOrderInputValue = "";

    switch (option) {
      case "-25bps":
        newLimitOrderInputValue = (bid * 0.9975).toFixed(priceDecimalPlaces);
        break;
      case "-10bps":
        newLimitOrderInputValue = (bid * 0.999).toFixed(priceDecimalPlaces);
        break;
      case "Bid":
        newLimitOrderInputValue = bid.toFixed(priceDecimalPlaces);
        break;
      case "Offer":
        newLimitOrderInputValue = offer.toFixed(priceDecimalPlaces);
        break;
      case "+10bps":
        newLimitOrderInputValue = (offer * 1.001).toFixed(priceDecimalPlaces);
        break;
      case "+25bps":
        newLimitOrderInputValue = (offer * 1.0025).toFixed(priceDecimalPlaces);
        break;
      default:
        console.warn("Invalid option");
        break;
    }

    if (newLimitOrderInputValue) {
      setLimitOrderPrice(newLimitOrderInputValue);
      handleValidateLimitOrderPrice(newLimitOrderInputValue);
    }
  };

  const handleSlippageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setAllowedSlippageInput(inputValue);
    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue)) {
      setAllowedSlippagePercentage(numericValue / 100);
    }
  };

  const handleBuySellChange = (newType: "buy" | "sell") => {
    resetForm();

    if (newType === "sell") {
      setTradeType("units");
    }
    setActionType(newType);
  };

  const getTotalToShow = (): string => {
    // If selected option is limitOrder we just return the limitOrderPrice times the amount
    if (orderType === "Limit" && tradeType === "units") {
      return (Number(limitOrderPrice) * Number(amount)).toFixed(2);
    }

    if (orderType === "Limit" && tradeType === "amount") {
      const minData = minMaxData[securityName]?.minSizeIncrement;
      const decimalPlaces = getDecimalPlaces(minData);
      return (Number(amount) / Number(limitOrderPrice)).toFixed(decimalPlaces);
    }

    // If tradeType is units we return the total price of the trade to display total price
    if (tradeType === "units") {
      return (unitPrice * Number(amount)).toFixed(2);
    }

    if (tradeType === "amount") {
      const minData = minMaxData[securityName]?.minSizeIncrement;
      const decimalPlaces = getDecimalPlaces(minData);
      // If tradeType is amount we return the amount to display the total quantity of the trade
      return (Number(amount) / unitPrice).toFixed(decimalPlaces);
    }

    return "-";
  };

  const totalToShow = getTotalToShow();

  return {
    amount,
    inputError,
    tradeType,
    orderType,
    setTradeType,
    handleOrderTypeChange,
    handleAmountChange,
    handleLimitOrderAmountChange,
    handleLimitOrderPriceChange,
    resetForm,
    handleShortcutClick,
    handleSlippageChange,
    allowedSlippageInput,
    limitOrderPrice,
    triggerPrice,
    setLimitOrderPrice,
    isLimitOnly,
    unitPriceWithSlippage,
    unitPrice,
    endTime,
    setEndTime,
    handleLimitShortcutClick,
    actionType,
    handleBuySellChange,
    totalToShow,
    handleTriggerPriceChange,
    setOrderType,
  };
};
